// Patients.tsx - Fixed version to prevent modal flicker

import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PatientList from "../components/patients/PatientList";
import PatientRegistrationForm from "../components/patients/PatientRegistrationForm";
import { Patient } from "../components/patients/PatientCard";
import PatientDetailsModal from "../components/patients/PatientDetailsModal";
import PatientService from "../services/patientService";

const Patients: React.FC = () => {
  const { patientId, tab } = useParams<{ patientId?: string; tab?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ FIX: Track the last loaded patient ID to prevent unnecessary refetches
  const lastLoadedPatientIdRef = useRef<number | null>(null);

  // Determine current view from URL
  const currentView = location.pathname.includes("/register")
    ? "register"
    : "list";

  // ✅ FIXED: Only load patient if ID actually changed
  useEffect(() => {
    if (patientId && !isNaN(Number(patientId))) {
      const id = parseInt(patientId);

      // ✅ FIX: Only fetch if patient ID changed, not on tab change
      if (lastLoadedPatientIdRef.current !== id) {
        setIsLoading(true);
        PatientService.getPatient(id)
          .then((patient) => {
            setSelectedPatient(patient);
            setIsPanelOpen(true);
            lastLoadedPatientIdRef.current = id; // ✅ Track loaded ID
          })
          .catch((error) => {
            console.error("Failed to load patient:", error);
            navigate("/patients", { replace: true });
            lastLoadedPatientIdRef.current = null;
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        // ✅ FIX: Same patient, just ensure modal is open
        if (!isPanelOpen) {
          setIsPanelOpen(true);
        }
      }
    } else if (!patientId) {
      // ✅ No patient ID in URL, close modal
      setIsPanelOpen(false);
      setSelectedPatient(null);
      lastLoadedPatientIdRef.current = null;
    }
  }, [patientId, navigate]); // ✅ Note: 'tab' is NOT in dependencies

  const handleViewPatient = (patient: Patient) => {
    // ✅ Set patient immediately to avoid flicker
    setSelectedPatient(patient);
    setIsPanelOpen(true);
    lastLoadedPatientIdRef.current = patient.id;

    // Then navigate
    navigate(`/patients/${patient.id}/overview`);
  };

  const handleEditPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsPanelOpen(true);
    lastLoadedPatientIdRef.current = patient.id;

    navigate(`/patients/${patient.id}/edit`);
  };

  const handleCloseSidePanel = () => {
    setIsPanelOpen(false);
    setSelectedPatient(null);
    lastLoadedPatientIdRef.current = null;

    navigate("/patients");
  };

  const handlePatientUpdated = (updatedPatient: Patient) => {
    setRefreshTrigger((prev) => prev + 1);
    // ✅ Update the selected patient without refetching
    setSelectedPatient(updatedPatient);
  };

  const handlePatientRegistered = (newPatient: Patient) => {
    setRefreshTrigger((prev) => prev + 1);
    setSelectedPatient(newPatient);
    setIsPanelOpen(true);
    lastLoadedPatientIdRef.current = newPatient.id;

    navigate(`/patients/${newPatient.id}/overview`);
  };

  const handleViewChange = (view: "list" | "register") => {
    if (view === "list") {
      navigate("/patients");
    } else {
      navigate("/patients/register");
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #E0F7FA 0%, #80DEEA 100%)",
  };

  const headerStyle: React.CSSProperties = {
    background: "#ffffff",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "16px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1e293b",
    margin: 0,
  };

  const navButtonsStyle: React.CSSProperties = {
    display: "flex",
    gap: "12px",
  };

  const getButtonStyle = (isActive: boolean): React.CSSProperties => ({
    padding: "10px 20px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: isActive ? "#3b82f6" : "#e2e8f0",
    color: isActive ? "#ffffff" : "#64748b",
  });

  const contentStyle: React.CSSProperties = {
    padding: currentView === "register" ? "0" : "24px",
  };

  return (
    <div style={containerStyle}>
      {/* Header Navigation */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>
          {currentView === "register"
            ? "Register New Patient"
            : "Patients Management"}
        </h1>

        <div style={navButtonsStyle}>
          <button
            style={getButtonStyle(currentView === "list")}
            onClick={() => handleViewChange("list")}
            onMouseEnter={(e) => {
              if (currentView !== "list") {
                e.currentTarget.style.background = "#cbd5e1";
              }
            }}
            onMouseLeave={(e) => {
              if (currentView !== "list") {
                e.currentTarget.style.background = "#e2e8f0";
              }
            }}
          >
            📋 Patient List
          </button>

          <button
            style={getButtonStyle(currentView === "register")}
            onClick={() => handleViewChange("register")}
            onMouseEnter={(e) => {
              if (currentView !== "register") {
                e.currentTarget.style.background = "#cbd5e1";
              }
            }}
            onMouseLeave={(e) => {
              if (currentView !== "register") {
                e.currentTarget.style.background = "#e2e8f0";
              }
            }}
          >
            ➕ Register Patient
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div style={contentStyle}>
        {currentView === "list" && (
          <PatientList
            onEditPatient={handleEditPatient}
            onViewPatient={handleViewPatient}
            refreshTrigger={refreshTrigger}
          />
        )}

        {currentView === "register" && (
          <PatientRegistrationForm onPatientUpdated={handlePatientRegistered} />
        )}
      </div>

      {/* ✅ FIX: Modal always rendered, controlled by isOpen prop */}
      <PatientDetailsModal
        patient={selectedPatient}
        isOpen={isPanelOpen}
        onClose={handleCloseSidePanel}
        onPatientUpdated={handlePatientUpdated}
        initialTab={tab || "overview"}
      />
    </div>
  );
};

export default Patients;
